<template>
  <sf-dialog v-model="dialog" scrollable @click:outside="cancel" @keydown.esc="cancel">
    <template #activator="slotData">
      <slot name="activator" v-bind="slotData" />
    </template>
    <v-card>
      <v-card-title ref="title">
        <sf-heading>{{ $t('contactData.contactData') }}</sf-heading>
      </v-card-title>
      <v-card-text class="pt-1">
        <v-form ref="form" v-model="valid" lazy-validation>
          <sf-text-field
            ref="first-name"
            v-model="contactData.firstName"
            :label="$t('contactData.firstName') + ' *'"
            :rules="[rules.requiredRule]"
            autocomplete="given-name"
          />
          <sf-text-field
            ref="last-name"
            v-model="contactData.lastName"
            :label="$t('contactData.lastName') + ' *'"
            :rules="[rules.requiredRule]"
            autocomplete="family-name"
          />
          <sf-text-field
            ref="phone"
            v-model="contactData.phone"
            :label="$t('user.phone')"
            :rules="[rules.phoneRule]"
            autocomplete="tel"
            @update:model-value="setPhoneNumber($event, 'phone')"
          />
          <sf-text-field
            ref="phoneMobile"
            v-model="contactData.phoneMobile"
            :label="$t('user.phoneMobile')"
            :rules="[rules.phoneRule]"
            autocomplete="tel"
            @update:model-value="setPhoneNumber($event, 'phoneMobile')"
          />
          <sf-text-field
            ref="email"
            v-model="contactData.email"
            :label="$t('contactData.email') + ' *'"
            :rules="[rules.mailRule, rules.requiredRule]"
            :disabled="disableEmailInput"
            autocomplete="email"
          />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <sf-text-button
          v-if="showDeleteButton"
          ref="delete-contact-data-button"
          color="error"
          @click="$emit('delete')"
        >
          {{ $t('deleteContactData') }}
        </sf-text-button>
        <v-spacer />
        <sf-text-button color="grey" class="text-medium-emphasis" @click="$emit('cancel')">
          {{ $t('cancel') }}
        </sf-text-button>
        <sf-text-button ref="save-contact-data-button" :disabled="!valid" @click="saveContactData">
          {{ $t('save') }}
        </sf-text-button>
      </v-card-actions>
    </v-card>
  </sf-dialog>
</template>

<script setup lang="ts">
import SfTextButton from '@/components/button/SfTextButton.vue'
import SfDialog from '@/components/dialog/SfDialog.vue'
import SfTextField from '@/components/input/SfTextField.vue'
import SfHeading from '@/components/text/SfHeading.vue'
import { parsePhoneNumber } from '@/modules/address/helpers'
import rules from '@/rules'
import type { VForm } from '@/types'
import { ContactData } from '@/types/index'
import { clone } from 'lodash-es'
import { PropType, ref, watch } from 'vue'

const props = defineProps({
  contact: {
    type: Object as PropType<ContactData>,
    required: true
  },
  showDeleteButton: {
    type: Boolean,
    default: true
  },
  disableEmailInput: {
    type: Boolean,
    default: false
  }
})

const emits = defineEmits(['delete', 'save', 'cancel'])
const dialog = ref(false)
const form = ref<VForm>()
const valid = ref(false)
const contactData = ref(clone(props.contact))

const resetForm = () => {
  if (form.value) {
    form.value.resetValidation()
  }
}

const cancel = () => {
  emits('cancel')
  dialog.value = false
}

const saveContactData = () => {
  if (form.value?.validate()) {
    emits('save', {
      ...contactData.value
    })
  }
}

watch(dialog, (newValue) => {
  if (newValue === true) {
    contactData.value = clone(props.contact)
    resetForm()
  }
})

const setPhoneNumber = (
  number: string | number | undefined | null,
  type: 'phone' | 'phoneMobile'
) => {
  contactData.value[type] = parsePhoneNumber(number || undefined)
}
</script>
